import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { CtaButtonComponent } from '../../../shared/components/ui/cta-button/cta-button.component';
import { PatrolPlatformFlagsAdminService } from '../../patrol/services/patrol-platform-flags-admin.service';
import type { PatrolPlatformFlags } from '../../patrol/services/patrol-platform-flags.service';
import {
  PatrolFleetGuild,
  PatrolRobloxBridgeStatus,
  PatrolService,
} from './services/patrol.service';
import { AdminPageShellComponent } from '../../../shared/components/admin/admin-page-shell/admin-page-shell.component';
import { AdminToggleSwitchComponent } from '../../../shared/components/admin/admin-toggle-switch/admin-toggle-switch.component';

export type AdminPatrolFleetTab = 'discord' | 'roblox';

@Component({
  selector: 'app-patrol',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    MatProgressSpinnerModule,
    CtaButtonComponent,
    AdminPageShellComponent,
    AdminToggleSwitchComponent,
  ],
  templateUrl: './patrol.component.html',
  styleUrl: './patrol.component.scss',
})
export class PatrolComponent implements OnInit {
  #patrolService = inject(PatrolService);
  #platformFlagsAdmin = inject(PatrolPlatformFlagsAdminService);

  platformFlags = signal<PatrolPlatformFlags | null>(null);
  platformFlagsLoading = signal(true);
  platformFlagsError = signal(false);
  platformToggleBusy = signal<'discord' | 'roblox' | null>(null);
  activeFleetTab = signal<AdminPatrolFleetTab>('discord');

  readonly fleetTabs: ReadonlyArray<{
    id: AdminPatrolFleetTab;
    labelKey: string;
  }> = [
    { id: 'discord', labelKey: 'app.admin.patrol.fleetTabs.discord' },
    { id: 'roblox', labelKey: 'app.admin.patrol.fleetTabs.roblox' },
  ];

  fleet = computed(() => this.#patrolService.fleetQuery.data());
  setup = computed(() => this.#patrolService.setupQuery.data());
  robloxFleet = computed(() => this.#patrolService.robloxFleetQuery.data());

  isDiscordLoading = computed(
    () =>
      this.#patrolService.fleetQuery.isPending() ||
      this.#patrolService.setupQuery.isPending(),
  );

  isDiscordError = computed(
    () =>
      this.#patrolService.fleetQuery.isError() &&
      this.#patrolService.setupQuery.isError(),
  );

  isRobloxFleetLoading = computed(() =>
    this.#patrolService.robloxFleetQuery.isPending(),
  );

  isRobloxFleetError = computed(() =>
    this.#patrolService.robloxFleetQuery.isError(),
  );

  registeredExperiences = computed(
    () => this.robloxFleet()?.experiences ?? [],
  );

  robloxAllowlistChips = computed(() => {
    const fleet = this.robloxFleet();
    if (!fleet?.allowlistEnforced) {
      return [];
    }

    const nameById = new Map(
      fleet.experiences
        .filter((experience) => experience.universeId)
        .map((experience) => [experience.universeId!, experience.experienceName]),
    );
    return fleet.allowedUniverseIds.map((universeId) => ({
      universeId,
      experienceName: nameById.get(universeId) ?? null,
    }));
  });

  robloxAllowlistPendingCount = computed(() => {
    const fleet = this.robloxFleet();
    if (!fleet?.allowlistEnforced) {
      return 0;
    }
    return fleet.experiences.filter(
      (experience) =>
        experience.universeId &&
        !experience.allowlisted &&
        !experience.ingestAllowed,
    ).length;
  });

  robloxIngestEnvEnabled = computed(() =>
    Boolean(this.robloxFleet()?.ingestEnabled),
  );

  robloxPlatformEnabled = computed(
    () => this.platformFlags()?.roblox ?? false,
  );

  robloxIngestEffective = computed(
    () => this.robloxIngestEnvEnabled() && this.robloxPlatformEnabled(),
  );

  robloxIngestStatusKey = computed(() => {
    if (!this.robloxIngestEnvEnabled()) {
      return 'app.admin.patrol.robloxFleet.ingestDisabledEnv';
    }
    if (!this.robloxPlatformEnabled()) {
      return 'app.admin.patrol.robloxFleet.ingestDisabledPlatform';
    }
    return 'app.admin.patrol.robloxFleet.ingestEnabled';
  });

  botInstalled = computed(
    () =>
      this.fleet()?.botInstalled ??
      this.setup()?.botInstalled ??
      false,
  );

  generatedInviteUrl = signal<string | null>(null);
  inviteErrorKey = signal<string | null>(null);
  allowlistActionGuildId = signal<string | null>(null);
  allowlistActionUniverseId = signal<string | null>(null);
  allowlistErrorKey = signal<string | null>(null);
  registeredGuilds = computed(() => this.fleet()?.guilds ?? []);
  unregisteredGuilds = computed(
    () => this.fleet()?.unregisteredBotGuilds ?? [],
  );

  statusLabelKey(status: PatrolFleetGuild['status']): string {
    return `app.admin.patrol.fleet.status.${status}`;
  }

  bridgeStatusLabelKey(
    status: PatrolRobloxBridgeStatus | null | undefined,
  ): string {
    return `app.admin.patrol.robloxFleet.bridgeStatus.${status ?? 'never_seen'}`;
  }

  formatBridgeLastSeen(value: string | null | undefined): string {
    if (!value) {
      return '—';
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
  }

  setFleetTab(tab: AdminPatrolFleetTab): void {
    this.activeFleetTab.set(tab);
  }

  isFleetTabActive(tab: AdminPatrolFleetTab): boolean {
    return this.activeFleetTab() === tab;
  }

  ngOnInit(): void {
    void this.loadPlatformFlags();
  }

  async loadPlatformFlags(): Promise<void> {
    this.platformFlagsLoading.set(true);
    this.platformFlagsError.set(false);
    try {
      this.platformFlags.set(await this.#platformFlagsAdmin.getPlatformFlags());
    } catch {
      this.platformFlagsError.set(true);
    } finally {
      this.platformFlagsLoading.set(false);
    }
  }

  isPlatformToggleBusy(platform: 'discord' | 'roblox'): boolean {
    return this.platformToggleBusy() === platform;
  }

  async toggleDiscordPlatform(enabled: boolean): Promise<void> {
    this.platformToggleBusy.set('discord');
    try {
      await this.#platformFlagsAdmin.setDiscordEnabled(enabled);
      this.platformFlags.update((flags) =>
        flags ? { ...flags, discord: enabled } : flags,
      );
    } catch {
      this.platformFlagsError.set(true);
    } finally {
      this.platformToggleBusy.set(null);
    }
  }

  async toggleRobloxPlatform(enabled: boolean): Promise<void> {
    this.platformToggleBusy.set('roblox');
    try {
      await this.#platformFlagsAdmin.setRobloxEnabled(enabled);
      this.platformFlags.update((flags) =>
        flags ? { ...flags, roblox: enabled } : flags,
      );
    } catch {
      this.platformFlagsError.set(true);
    } finally {
      this.platformToggleBusy.set(null);
    }
  }

  async addBotToServer(): Promise<void> {
    this.inviteErrorKey.set(null);

    let inviteUrl =
      this.generatedInviteUrl() ??
      this.fleet()?.inviteUrl ??
      this.setup()?.inviteUrl ??
      null;

    if (!inviteUrl) {
      await this.#patrolService.refreshAll();
      inviteUrl =
        this.#patrolService.fleetQuery.data()?.inviteUrl ??
        this.#patrolService.setupQuery.data()?.inviteUrl ??
        null;
    }

    if (!inviteUrl) {
      this.inviteErrorKey.set('app.admin.patrol.inviteUnavailable');
      return;
    }

    this.generatedInviteUrl.set(inviteUrl);
    window.open(inviteUrl, '_blank', 'noopener,noreferrer');
  }

  refreshStatus(): void {
    this.generatedInviteUrl.set(null);
    this.inviteErrorKey.set(null);
    this.allowlistErrorKey.set(null);
    void this.loadPlatformFlags();
    this.#patrolService.refreshAll();
  }

  isAllowlistBusy(guildId: string | null | undefined): boolean {
    return !!guildId && this.allowlistActionGuildId() === guildId;
  }

  isUniverseAllowlistBusy(universeId: string | null | undefined): boolean {
    return !!universeId && this.allowlistActionUniverseId() === universeId;
  }

  async allowGuild(guildId: string | null | undefined): Promise<void> {
    if (!guildId) {
      return;
    }

    this.allowlistErrorKey.set(null);
    this.allowlistActionGuildId.set(guildId);
    try {
      await this.#patrolService.addToAllowlist(guildId);
    } catch {
      this.allowlistErrorKey.set('app.admin.patrol.fleet.allowlistError');
    } finally {
      this.allowlistActionGuildId.set(null);
    }
  }

  async removeGuildFromAllowlist(guildId: string | null | undefined): Promise<void> {
    if (!guildId) {
      return;
    }

    this.allowlistErrorKey.set(null);
    this.allowlistActionGuildId.set(guildId);
    try {
      await this.#patrolService.removeFromAllowlist(guildId);
    } catch {
      this.allowlistErrorKey.set('app.admin.patrol.fleet.allowlistError');
    } finally {
      this.allowlistActionGuildId.set(null);
    }
  }

  async allowUniverse(universeId: string | null | undefined): Promise<void> {
    if (!universeId) {
      return;
    }

    this.allowlistErrorKey.set(null);
    this.allowlistActionUniverseId.set(universeId);
    try {
      await this.#patrolService.addUniverseToAllowlist(universeId);
    } catch {
      this.allowlistErrorKey.set('app.admin.patrol.fleet.allowlistError');
    } finally {
      this.allowlistActionUniverseId.set(null);
    }
  }

  async removeUniverseFromAllowlist(
    universeId: string | null | undefined,
  ): Promise<void> {
    if (!universeId) {
      return;
    }

    this.allowlistErrorKey.set(null);
    this.allowlistActionUniverseId.set(universeId);
    try {
      await this.#patrolService.removeUniverseFromAllowlist(universeId);
    } catch {
      this.allowlistErrorKey.set('app.admin.patrol.fleet.allowlistError');
    } finally {
      this.allowlistActionUniverseId.set(null);
    }
  }
}
